import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import BotonComposicion from '../components/svg/BotonComposicion';
import BotonTerritorios from '../components/svg/BotonTerritorios';
import BotonPartidos from '../components/svg/BotonPartidos';
import BotonInfoGeneral from '../components/svg/BotonInfoGeneral';
import { useNomenclator } from './nomenclatorContext';
import { usePage } from './pageContext';
import useIni from '../hooks/useIni';
import { MapProvider } from './mapContext';

const EleccionContext = React.createContext({});
const SetElectionOptions = React.createContext();

const tabs = [
  {
    id: 'composicion',
    title: 'Seleccionar composicion',
    element: props => <BotonComposicion {...props} />,
  },
  {
    id: 'votosPartidos',
    title: 'Seleccionar votos a partidos',
    element: props => <BotonPartidos {...props} />,
  },
  {
    id: 'votosPartidosConsultas',
    title: 'Seleccionar votos a partidos',
    element: props => <BotonPartidos {...props} />,
  },
  {
    id: 'votosPartidosCarousel',
    title: 'Seleccionar votos a partidos',
    element: props => <BotonPartidos {...props} />,
  },
  {
    id: 'territorios',
    title: 'Seleccionar territorios',
    element: props => <BotonTerritorios {...props} />,
  },
  {
    id: 'infoGeneral',
    title: 'Seleccionar información general',
    element: props => <BotonInfoGeneral {...props} />,
  },
];

const computeTabs = (idElec, level, isCircus) => {
  let activeTabs = tabs;
  const tabsTerritoriales = ['votosPartidos', 'infoGeneral', 'territorios'];
  // Cuando circus es 2, estamos por encima y se oculta la pestaña de votos a partidos
  if (isCircus === 2) {
    activeTabs = activeTabs.filter(t => t.id !== 'votosPartidos');
  }

  return activeTabs.filter(tab => tabsTerritoriales.includes(tab.id));
};

const tableOptions = (elec, level, isCircus, selectedTab) => {
  const curules = isCircus > 0;
  const votos = selectedTab.id !== 'composicion';
  const mostrarCandidatos = !(
    isCircus === 2 && selectedTab.id !== 'composicion'
  );
  return {
    mostrarVotos: votos,
    mostrarCargos: curules,
    mostrarCandidatos: mostrarCandidatos,
  };
};

const getOptions = (data = {}, nomenclator, camaraID, tabID) => {
  const level = nomenclator.ambitos[data.elec]?.find(amb => amb.c === data.amb)
    ?.l;
  const selectedCamara =
    (camaraID && data.camaras?.find(c => c.cam === camaraID)) ??
    data.camaras?.[0] ??
    null;
  const isCircus = selectedCamara?.cir ?? data.camaras?.[0]?.cir ?? false;
  const activeTabs = computeTabs(
    Number(data.elec),
    Number(level),
    Number(isCircus)
  );
  const selectedTab =
    (tabID && activeTabs.find(tab => tab.id === tabID)) || activeTabs[0];
  const options = {
    idEleccion: data.elec,
    camaras: data.camaras,
    bogota: !!data?.bogota && data.bogota,
    selectedCamara: selectedCamara ?? data.camaras?.[0] ?? null,
    mapData: selectedCamara ? selectedCamara.mapagan : [],
    tabs: activeTabs,
    selectedTab: activeTabs[0],
    tableOptions: tableOptions(
      Number(data.elec),
      Number(level),
      Number(isCircus),
      selectedTab
    ),
  };

  return options;
};

export function EleccionProvider({ children }) {
  const [elecOptions, setElecOptions] = useState(null);
  const ini = useIni();
  const nomenclator = useNomenclator();
  const pageState = usePage();
  const { data, urlInfo, requestedUrlInfo } = pageState;

  const _data = useMemo(
    () =>
      Array.isArray(data) ? data.find(d => d.elec === urlInfo.eleccion) : data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, urlInfo?.eleccion]
  );

  useEffect(() => {
    if (
      (!pageState.isLoading && _data && !elecOptions) ||
      (elecOptions && elecOptions.idEleccion !== requestedUrlInfo.eleccion)
    ) {
      setElecOptions(getOptions(_data, nomenclator, null, null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_data, elecOptions?.selectedCamara, elecOptions?.selectedTab]);

  useEffect(() => {
    if (
      !pageState.isLoading &&
      elecOptions &&
      data.elec === elecOptions.idEleccion
    ) {
      setElecOptions(
        getOptions(
          _data,
          nomenclator,
          elecOptions.selectedCamara?.cam,
          elecOptions.selectedTab?.id
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_data]);

  const setElecOptionsCtx = useCallback(
    v => {
      setElecOptions(
        v ??
          getOptions(
            _data,
            nomenclator,
            null,
            null,
            ini.showCurules && data?.shc === '1'
          )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_data, ini.showCurules, nomenclator, urlInfo?.eleccion]
  );

  return (
    <EleccionContext.Provider value={elecOptions}>
      <SetElectionOptions.Provider value={setElecOptionsCtx}>
        <MapProvider>{children}</MapProvider>
      </SetElectionOptions.Provider>
    </EleccionContext.Provider>
  );
}

export function useEleccion() {
  const context = useContext(EleccionContext);
  return context;
}

export function useSetEleccion() {
  const context = useContext(SetElectionOptions);
  return context;
}
